import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyIcon, MoreVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import type { TaxonomyDataset } from '@/entities/taxonomy-dataset';

interface DatasetActionMenuProps {
  dataset: TaxonomyDataset;
  canManage: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  duplicating?: boolean;
}

/**
 * The row's overflow menu.
 *
 * The `stopPropagation` wrappers are not defensive noise. React portals bubble
 * events through the REACT tree rather than the DOM tree, so without them a
 * click anywhere inside the menu — or inside a dialog the menu opens — reaches
 * the row's `onClick` and navigates away mid-action. The canonical
 * `TaxonomyActionMenu` carries the same pair for the same reason.
 *
 * Write items are OMITTED rather than disabled when the user cannot manage:
 * a control someone can never get back is not worth showing them.
 */
export function DatasetActionMenu({
  dataset,
  canManage,
  onEdit,
  onDuplicate,
  onDelete,
  duplicating = false,
}: DatasetActionMenuProps) {
  const { t } = useTranslation('knowledge');
  const [open, setOpen] = useState(false);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- see the note above: this only cancels a bubble the row would otherwise act on.
    <span className="contents" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="mini"
            aria-label={t('taxonomy.generationList.rowActions', { name: dataset.name })}
            className="size-9 p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVerticalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onSelect={onEdit}>
            <PencilIcon className="mr-2 size-4" />
            {t('taxonomy.generationList.edit')}
          </DropdownMenuItem>
          {canManage && (
            <DropdownMenuItem onSelect={onDuplicate} disabled={duplicating}>
              <CopyIcon className="mr-2 size-4" />
              {t('taxonomy.generationList.duplicate')}
            </DropdownMenuItem>
          )}
          {canManage && (
            <DropdownMenuItem onSelect={onDelete} variant="destructive">
              <Trash2Icon className="mr-2 size-4" />
              {t('taxonomy.generationList.delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
